import {
  modelCatalogueOf,
  servedWorldsOf,
  panoVariantsOf,
  validateLesson,
  type FurnitureAssigned,
  withCurrentWorldNames,
} from '@vrlearning/lesson-schema';
import { catalogueOfServed } from './api/served-catalogue.js';
import type { LoadedLesson } from './api/lessons-client.js';
import { furnitureAssignedOf } from './api/session-client.js';
import { cuesFrom } from './engine/guide.js';

/**
 * Playing one lesson from a preview ticket (ADR-029).
 *
 * The console opens this app in a new tab at `?play=<ticket>` so that a
 * person -- the platform admin with a generated lesson, a teacher with one of
 * the school's -- can go through a lesson exactly as a class would. The
 * ticket is the only credential: good once and for a minute, it fetches one
 * lesson body and its signed media, and nothing else. No pairing, no session,
 * nothing recorded; `bootPlay` in main.ts does the playing.
 *
 * This is the part that can be tested without a browser: reading the address,
 * fetching the lesson, and what to say when there is none.
 */

/** What the viewer reads. An adult, so "u", as in the console. */
export const PLAY_TEXT = {
  loading: 'De les wordt geladen…',
  gone: {
    title: 'Deze link werkt niet meer',
    body:
      'Een link om een les te bekijken werkt één keer, en maar een minuut lang. ' +
      'Vraag in de console een nieuwe aan met "Bekijk in de browser".',
  },
  failed: { title: 'Deze les kan niet starten' },
  ended: {
    title: 'Einde van de les',
    body:
      'Dit was een voorbeeld: er is niets opgeslagen, geen sessie en geen antwoorden. ' +
      'U kunt dit tabblad sluiten.',
  },
  again: 'Opnieuw bekijken',
} as const;

export type PlayOutcome =
  /** `furniture`: a villa lesson's meubelset, when the answer carries one (ADR-037; the dev fixture). */
  | { kind: 'ready'; loaded: LoadedLesson; furniture?: FurnitureAssigned }
  /** Used, expired, or never issued -- the API does not say which, on purpose. */
  | { kind: 'gone' }
  | { kind: 'failed'; detail: string };

export type PlayProblem = Exclude<PlayOutcome, { kind: 'ready' }>;

/**
 * What the address says this tab is for: a ticket to redeem, or a tab that
 * redeemed one before and has been reloaded since.
 */
export type PlayStart = { kind: 'ticket'; ticket: string } | { kind: 'spent' };

/** Reads `?play=<ticket>` off the page's own address. */
export function playRequest(
  href: string = globalThis.location?.href ?? '',
): { ticket: string } | undefined {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return undefined;
  }
  const ticket = url.searchParams.get('play');
  return ticket ? { ticket } : undefined;
}

/**
 * Marks the history entry a ticket was read from. The entry's state outlives
 * a reload while the address no longer says anything, and without the mark a
 * reloaded tab would boot as a headset -- the pairing screen on a laptop, and
 * on a paired Quest a check-in and a socket to the school's classes.
 */
const PLAYED = 'vrlearningPlayed';

/**
 * Reads the ticket and takes it straight back off the address: replaced, not
 * pushed, so neither the address bar nor the tab's history keeps it, and the
 * fetch that redeems it goes out with the bare address as its referrer. A
 * used ticket is worthless, but an address that looks like a lesson link and
 * is not is one somebody will bookmark or paste.
 */
export function takePlayRequest(
  place: { href: string } | undefined = globalThis.location,
  history: Pick<History, 'state' | 'replaceState'> | undefined = globalThis.history,
): PlayStart | undefined {
  const href = place?.href ?? '';
  const request = playRequest(href);
  if (request) {
    history?.replaceState({ [PLAYED]: true }, '', new URL(href).pathname);
    return { kind: 'ticket', ticket: request.ticket };
  }
  const state: unknown = history?.state;
  if (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[PLAYED] === true
  ) {
    return { kind: 'spent' };
  }
  return undefined;
}

/**
 * Redeems the ticket for its lesson, validated before it can reach the
 * renderer -- the same check every other lesson passes (lessons-client.ts).
 */
export async function fetchPlayLesson(
  ticket: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<PlayOutcome> {
  let response: Response;
  try {
    // Uncached: the answer is good once, and a stored copy would replay it.
    response = await fetchImpl(`/v1/lesson-preview/${encodeURIComponent(ticket)}`, {
      cache: 'no-store',
    });
  } catch {
    return { kind: 'failed', detail: 'geen verbinding met de server' };
  }

  if (response.status === 404) return { kind: 'gone' };
  if (!response.ok) return { kind: 'failed', detail: `fout ${response.status}` };

  let payload: {
    lesson?: unknown;
    media?: Record<string, string>;
    cues?: unknown;
    furniture?: unknown;
    variants?: unknown;
    models?: unknown;
    worlds?: unknown;
  };
  try {
    payload = (await response.json()) as typeof payload;
  } catch {
    return { kind: 'failed', detail: 'onleesbaar antwoord van de server' };
  }

  const catalogue = catalogueOfServed(servedWorldsOf(payload.worlds));
  const result = validateLesson(payload.lesson, { worlds: catalogue });
  if (!result.valid) {
    const first = result.errors[0];
    return {
      kind: 'failed',
      detail: `les is ongeldig: ${first?.path ?? ''} ${first?.message ?? ''}`.trim(),
    };
  }
  const furniture = furnitureAssignedOf(payload.furniture);
  return {
    kind: 'ready',
    loaded: {
      // A world's old names played as its current ones (spec 2026-10-16).
      lesson: withCurrentWorldNames(result.lesson, catalogue),
      media: payload.media ?? {},
      cues: cuesFrom(payload.cues),
      variants: panoVariantsOf(payload.variants),
      models: modelCatalogueOf(payload.models),
      worlds: servedWorldsOf(payload.worlds),
    },
    ...(furniture ? { furniture } : {}),
  };
}

/** The card for a lesson that cannot be played. */
export function playProblemText(problem: PlayProblem): { title: string; body: string } {
  if (problem.kind === 'gone') return PLAY_TEXT.gone;
  return {
    title: PLAY_TEXT.failed.title,
    body: `De les kon niet worden geladen (${problem.detail}). Probeer het opnieuw vanuit de console.`,
  };
}
